// src/pages/cuentas-bancarias/EditarCuenta.js
import React, { useState, useEffect } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import { FormLabel, Offcanvas } from 'react-bootstrap'
import { PanelFooter, PanelHeader } from 'components/Panel'
import { normalizarBooleano } from 'utils/general'

function EditarCuenta(props) {
	const { cuentaOriginal, mostrar, guardar, cancelar } = props

	const [datosCuenta, setDatosCuenta] = useState({
		id: null,
		banco: '',
		numero_cuenta: '',
		tipo_cuenta: 'Cuenta Corriente',
		titular: '',
		ruc: '',
		moneda: 'Gs.',
		activo: true
	})

	const [erroresValidacion, setErroresValidacion] = useState({})
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		if (cuentaOriginal && mostrar) {
			setDatosCuenta({
				id: cuentaOriginal.id,
				banco: cuentaOriginal.banco || '',
				numero_cuenta: cuentaOriginal.numero_cuenta || '',
				tipo_cuenta: cuentaOriginal.tipo_cuenta || 'Cuenta Corriente',
				titular: cuentaOriginal.titular || '',
				ruc: cuentaOriginal.ruc || '',
				moneda: cuentaOriginal.moneda || 'Gs.',
				activo: normalizarBooleano(cuentaOriginal.activo)
			})
			setErroresValidacion({})
		}
	}, [cuentaOriginal, mostrar])

	const handleClose = () => {
		setDatosCuenta({
			id: null,
			banco: '',
			numero_cuenta: '',
			tipo_cuenta: 'Cuenta Corriente',
			titular: '',
			ruc: '',
			moneda: 'Gs.',
			activo: true
		})
		setErroresValidacion({})
		cancelar()
	}

	const handleChange = (value, key) => {
		setDatosCuenta(prev => ({
			...prev,
			[key]: value
		}))
	}

	const validarDatos = () => {
		const nuevosErrores = {}

		if (!datosCuenta.banco.trim()) {
			nuevosErrores.banco = 'El banco es requerido'
		}

		if (!datosCuenta.numero_cuenta.trim()) {
			nuevosErrores.numero_cuenta = 'El número de cuenta es requerido'
		}

		if (!datosCuenta.tipo_cuenta.trim()) {
			nuevosErrores.tipo_cuenta = 'El tipo de cuenta es requerido'
		}

		if (!datosCuenta.titular.trim()) {
			nuevosErrores.titular = 'El titular es requerido'
		}

		setErroresValidacion(nuevosErrores)
		return Object.keys(nuevosErrores).length === 0
	}

	const handleProcesar = async (e) => {
		e.preventDefault()

		if (validarDatos()) {
			setLoading(true)
			try {
				const { id, ...datosActualizados } = datosCuenta
				// Normalizar booleano antes de enviar
				datosActualizados.activo = normalizarBooleano(datosActualizados.activo) ? 1 : 0
				await guardar(datosActualizados)
				handleClose()
			} catch (error) {
				console.error(error)
			} finally {
				setLoading(false)
			}
		}
	}

	// render
	return (
		<Offcanvas show={mostrar} onHide={handleClose} placement="end" style={{ width: '50%' }}>
			<PanelHeader onClick={handleClose} iconClass="fa-arrow-left" btnText="Atrás" text="Editar cuenta bancaria" />
			<Offcanvas.Body id="editar-cuenta">
				<h5 className="mb-3">Datos de la cuenta</h5>
				{datosCuenta.id && (
					<Row>
						<Col sm={12}>
							<Form onSubmit={handleProcesar}>
								<Row>
									<Col>
										<Form.Group className="mb-3" controlId="cuenta-activo">
											<Form.Check
												type="switch"
												label="Activa (visible para los clientes)"
												checked={datosCuenta.activo}
												onChange={(e) => handleChange(e.target.checked, 'activo')}
											/>
										</Form.Group>
									</Col>
								</Row>

								<Row>
									<Col md={6}>
										<Form.Group className="mb-3" controlId="cuenta-banco">
											<FormLabel>Banco *</FormLabel>
											<Form.Control
												type="text"
												value={datosCuenta.banco}
												onChange={(e) => handleChange(e.target.value, 'banco')}
												isInvalid={!!erroresValidacion.banco}
												placeholder="Ej: Banco Itaú"
											/>
											<Form.Control.Feedback type="invalid">
												{erroresValidacion.banco}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-3" controlId="cuenta-tipo">
											<FormLabel>Tipo de cuenta *</FormLabel>
											<Form.Select
												value={datosCuenta.tipo_cuenta}
												onChange={(e) => handleChange(e.target.value, 'tipo_cuenta')}
												isInvalid={!!erroresValidacion.tipo_cuenta}
											>
												<option value="Cuenta Corriente">Cuenta Corriente</option>
												<option value="Caja de Ahorro">Caja de Ahorro</option>
											</Form.Select>
											<Form.Control.Feedback type="invalid">
												{erroresValidacion.tipo_cuenta}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>
								</Row>

								<Row>
									<Col md={8}>
										<Form.Group className="mb-3" controlId="cuenta-numero">
											<FormLabel>Número de cuenta *</FormLabel>
											<Form.Control
												type="text"
												value={datosCuenta.numero_cuenta}
												onChange={(e) => handleChange(e.target.value, 'numero_cuenta')}
												isInvalid={!!erroresValidacion.numero_cuenta}
												placeholder="Ej: 1234567890"
											/>
											<Form.Control.Feedback type="invalid">
												{erroresValidacion.numero_cuenta}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>
									<Col md={4}>
										<Form.Group className="mb-3" controlId="cuenta-moneda">
											<FormLabel>Moneda</FormLabel>
											<Form.Select
												value={datosCuenta.moneda}
												onChange={(e) => handleChange(e.target.value, 'moneda')}
											>
												<option value="Gs.">Guaraníes (Gs.)</option>
												<option value="USD">Dólares (USD)</option>
											</Form.Select>
										</Form.Group>
									</Col>
								</Row>

								<Row>
									<Col md={8}>
										<Form.Group className="mb-3" controlId="cuenta-titular">
											<FormLabel>Titular *</FormLabel>
											<Form.Control
												type="text"
												value={datosCuenta.titular}
												onChange={(e) => handleChange(e.target.value, 'titular')}
												isInvalid={!!erroresValidacion.titular}
												placeholder="Nombre de la empresa o persona"
											/>
											<Form.Control.Feedback type="invalid">
												{erroresValidacion.titular}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>
									<Col md={4}>
										<Form.Group className="mb-3" controlId="cuenta-ruc">
											<FormLabel>RUC (opcional)</FormLabel>
											<Form.Control
												type="text"
												value={datosCuenta.ruc}
												onChange={(e) => handleChange(e.target.value, 'ruc')}
												placeholder="Ej: 80012345-6"
											/>
										</Form.Group>
									</Col>
								</Row>
							</Form>
						</Col>
					</Row>
				)}
			</Offcanvas.Body>
			<PanelFooter>
				<Button variant="outline-secondary" onClick={handleClose} className="me-2" disabled={loading}>
					Cancelar
				</Button>
				<Button
					variant="primary"
					onClick={handleProcesar}
					disabled={loading}
				>
					{loading ? (
						<><i className="fa fa-spinner fa-spin me-2"></i> Guardando...</>
					) : (
						<><i className="fa fa-save me-2"></i> Guardar</>
					)}
				</Button>
			</PanelFooter>
		</Offcanvas>
	)
}

export default EditarCuenta
